import { createSelector } from 'reselect'
import type { Artist } from 'types/artist'
import type { Brief } from 'types/brief'
import type { Playlist } from 'types/playlist'
import type { Selection } from 'types/selection'
import type { State } from 'types/redux'
import type { Track } from 'types/track'
import { BRIEF, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import { getArtists } from '@disco/state-management/selectors/artists'
import { getExplorerPanes } from '@disco/state-management/selectors/explorer'
import { getGroups } from '@disco/state-management/selectors/groups'
import { getPlaylists } from '@disco/state-management/selectors/playlists'
import {
  getShortlistsObject,
  getVisibleShortlistIds,
} from '@disco/state-management/selectors/shortlists'
import { getTracks } from '@disco/state-management/selectors/tracks'
import { getAllBriefs } from '@disco/state-management/reducers/entities/briefs'
import omit from 'lodash/omit'

const EMPTY_ARRAY: any[] = []

/**
 * Gets filtered selection state, optionally excluding explorer selections.
 * @param state - The Redux state
 * @param onlyExplorer - If true, excludes explorer selections from the result
 * @returns Filtered selection state object
 */
const getFilteredSelection = createSelector(
  [
    (state: State) => state.selection,
    getVisibleShortlistIds,
    (_state: State, onlyExplorer?: boolean) => onlyExplorer,
  ],
  (selections, shortlistIds, onlyExplorer): State['selection'] => {
    if (!onlyExplorer) return selections
    return omit(selections, shortlistIds)
  },
)

/**
 * Gets all selection state from the Redux state.
 * @param state - The Redux state
 * @returns The complete selection state object
 */
export const getAllSelection = (state: State): State['selection'] => {
  return state.selection
}
/**
 * Gets selection state for explorer panes only.
 * @param state - The Redux state
 * @returns Explorer selection state object
 */
export const getExplorerSelection = (state: State): State['selection'] => {
  return getFilteredSelection(state, true)
}
/**
 * Gets selection state for shortlists.
 * @param state - The Redux state
 * @returns Shortlists selection state object
 */
export const getShortlistsSelection = (state: State): State['selection'] => {
  return getFilteredSelection(state, false)
}

export const getDataTableSelection = (
  state: State,
  selectionId: string | null | undefined,
): State['selection'][string] | null => {
  if (!selectionId) return null
  return state.selection?.[selectionId] || null
}
/**
 * Gets the selected indexes for a specific selection ID.
 * @param state - The Redux state
 * @param selectionId - The selection ID to get indexes for
 * @returns Array of selected indexes
 */
export const getSelectionIndexes = (
  state: State,
  selectionId: string | null | undefined,
): number[] => {
  if (!selectionId) return EMPTY_ARRAY
  return state.selection?.[selectionId]?.indexes || EMPTY_ARRAY
}

/**
 * Filters items to only include item type.
 * @param items - Array of mixed items
 * @returns Array containing only items of the specified type
 */
const onlyItemType =
  (type: string) =>
  (items): [] =>
    items.filter(item => item.type && item.type === type)

/**
 * Gets the explorer pane that has active selections.
 * @param state - The Redux state
 * @returns The explorer pane with active selection, or null if none
 */
export const getExplorerSelectedPane = createSelector(
  [getExplorerSelection, getExplorerPanes],
  (explorerSelection, explorerPanes) => {
    let activeSelection: Selection | null = null

    for (const selectionId in explorerSelection) {
      const selection = explorerSelection[selectionId]

      if (selection.indexes.length !== 0) {
        activeSelection = selection
        break
      }
    }

    if (typeof activeSelection?.paneIndex !== 'number') return null
    return explorerPanes[activeSelection.paneIndex]
  },
)
/**
 * Gets the group associated with the selected explorer pane.
 * @param state - The Redux state
 * @returns The group object for the selected pane, or null if none
 */
export const getExplorerSelectionGroup = createSelector(
  [getExplorerSelectedPane, getGroups],
  (pane, groups) => {
    if (!pane || !pane.groupId || groups.length === 0) return null
    return groups.find(g => g.id === pane.groupId)
  },
)

/**
 * Gets items from a pane based on selected indexes.
 * @param resources - Object containing tracks, playlists, and briefs
 * @param selectedIndexes - Array of selected indexes
 * @param itemList - List of items to select from
 * @returns Array of selected items (tracks, playlists, or briefs)
 */
const getPaneItems = ({ tracks, playlists }, selectedIndexes, itemList) => {
  const emptySelection = !selectedIndexes || !selectedIndexes.length
  const emptyItems = !itemList || !itemList.length
  if (emptySelection || emptyItems) return []
  return selectedIndexes
    .map(index => {
      const resultItem = itemList[index]
      if (!resultItem) return null
      if (resultItem.type === PLAYLIST) {
        return playlists[resultItem.playlist_id]
      }

      if (resultItem.type === TRACK) {
        return resultItem.tempId ? resultItem : tracks[resultItem.id]
      }

      if (resultItem.type === BRIEF) {
        return resultItem
      }

      return null
    })
    .filter(Boolean)
}

/**
 * Gets all selected items from explorer panes.
 * @param state - The Redux state
 * @returns Array of selected items (tracks, playlists, artists, or briefs)
 */
export const getExplorerSelectedItems: (
  arg0: State,
) => (Track | Playlist | Artist | Brief)[] = createSelector(
  [getArtists, getTracks, getPlaylists, getAllBriefs, getExplorerSelection],
  (artists, tracks, playlists, briefs, explorerSelection) => {
    const result: any[] = []

    for (const selectionId in explorerSelection) {
      if (explorerSelection[selectionId]) {
        const selection = explorerSelection[selectionId]
        const selectedIndexes = selection?.indexes || EMPTY_ARRAY
        const selectionItems = selection?.selectionItems || EMPTY_ARRAY

        const itemList: any[] = []

        selectionItems.forEach(item => {
          switch (item.type) {
            case 'artist':
              itemList.push(artists[item.id])
              break

            case 'track':
              itemList.push(tracks[item.id])
              break

            case 'brief':
              itemList.push(briefs[item.id])
              break

            default:
              itemList.push(playlists[item.id])
          }
        })

        // TODO: check for regressions https://bitbucket.org/leveltwomusic/disco-apps/commits/1e956f7eceb6f6f92723f2c2223905a673567caf
        // const explorerPane = explorerPanes[pane]
        // if (!explorerPane) continue
        result.push(
          ...getPaneItems(
            {
              tracks,
              playlists,
            },
            selectedIndexes,
            itemList,
          ),
        ) // TODO: Enable multi-select for artist later
        // use new implementation from https://bitbucket.org/leveltwomusic/disco-apps/pull-requests/1907
        // if (explorerPane.type === ARTIST_PANE) {
        //   const artist = artists.items[explorerPane.artistId]
        //   let itemList = []
        //   if (artist?.showcase_playlist_ids) {
        //     itemList.push(
        //       ...artist.showcase_playlist_ids.map(id => ({
        //         playlist_id: id,
        //         type: PLAYLIST,
        //       })),
        //     )
        //   }
        //   if (artist?.track_ids) {
        //     itemList.push(
        //       ...artist.track_ids.map(id => ({
        //         id,
        //         type: TRACK,
        //       })),
        //     )
        //   }
        //   result.push(
        //     ...getPaneItems({ tracks, playlists }, selectedIndexes, itemList),
        //   )
        // }
      }
    }

    // don't return new array to prevent selectors from recomputing
    // if nothing is selected
    if (result.length === 0) return EMPTY_ARRAY
    return result
  },
)
/**
 * Gets only the selected tracks from explorer panes.
 * @param state - The Redux state
 * @returns Array of selected track objects
 */
export const getExplorerSelectedTracks = createSelector(
  getExplorerSelectedItems,
  onlyItemType(TRACK),
)
/**
 * Gets only the selected playlists from explorer panes.
 * @param state - The Redux state
 * @returns Array of selected playlist objects
 */
export const getExplorerSelectedPlaylists = createSelector(
  getExplorerSelectedItems,
  onlyItemType(PLAYLIST),
)
/**
 * Gets only the selected briefs from explorer panes.
 * @param state - The Redux state
 * @returns Array of selected brief objects
 */
export const getExplorerSelectedBriefs = createSelector(
  getExplorerSelectedItems,
  onlyItemType(BRIEF),
)
/**
 * Gets all selected items from shortlists.
 * @param state - The Redux state
 * @returns Array of selected track objects from shortlists
 */
export const getShortlistsSelectedItems = createSelector(
  [getTracks, getShortlistsObject, getShortlistsSelection],
  (tracks, shortlists, shortlistsSelection) => {
    const result: Track[] = []

    for (const selectionId in shortlistsSelection) {
      if (shortlistsSelection[selectionId]) {
        const selectedIndexes =
          shortlistsSelection[selectionId]?.indexes || EMPTY_ARRAY
        const shortlistItems = shortlists[selectionId]?.items || EMPTY_ARRAY
        if (!shortlistItems) continue
        const trackItems = getPaneItems(
          {
            tracks,
            playlists: {},
          },
          selectedIndexes,
          shortlistItems.map(item => item.track),
        ) as any as Track[]
        result.push(...trackItems)
      }
    }

    // don't return new array to prevent selectors from recomputing
    // if nothing is selected
    if (result.length === 0) return EMPTY_ARRAY
    return result
  },
)
/**
 * Gets all selected items from both explorer and shortlists.
 * @param state - The Redux state
 * @returns Combined array of all selected items
 */
export const getAllSelectedItems = createSelector(
  [getExplorerSelectedItems, getShortlistsSelectedItems],
  (explorerItems, shortlistItems) => {
    return [...explorerItems, ...shortlistItems]
  },
)
/**
 * Gets the total count of all selected items.
 * @param state - The Redux state
 * @returns Number of selected items
 */
export const getAllSelectedItemsCount = createSelector(
  [getAllSelectedItems],
  items => items.length,
)
/**
 * Gets the first 5 selected items for preview purposes.
 * @param state - The Redux state
 * @returns Array of up to 5 selected items for preview
 */
export const getSelectedItemsForPreview = createSelector(
  [getAllSelectedItems],
  items => items.slice(0, 5),
)
/**
 * Factory function that creates a selector to get selection indexes.
 * @returns A selector function for getting selection indexes
 */
export const makeGetSelectionIndexes = (): typeof getSelectionIndexes =>
  getSelectionIndexes
/**
 * Factory function that creates a selector to get shortlists selected items.
 * @returns A selector function for getting shortlists selected items
 */
export const makeGetShortlistsSelectedItems =
  (): typeof getShortlistsSelectedItems => getShortlistsSelectedItems
